import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal, Upload, Button, Table, Tag, Alert, Descriptions, Select, Typography, Space, Checkbox, Input, Steps, Result, Tooltip, message,
} from 'antd';
import { InboxOutlined, RobotOutlined, CheckCircleOutlined, WarningOutlined, CloseCircleOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { aiOrderImportAPI, customerAPI, productAPI, shippingDestinationAPI } from '../services/api.ts';
import { generateProductDisplayName } from '../utils/productUtils.ts';

const { Dragger } = Upload;
const { Text } = Typography;

interface AIOrderImportModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Candidate { id: number; internal_product_code: string; size?: string | null; color?: string | null; product_description?: string | null; client_product_code?: string | null; }
interface Check { code: string; ok: boolean; message: string; }
interface AnalyzeLine {
  row: number;
  customer_product_code: string | null;
  internal_product_code: string | null;
  description: string | null;
  quantity: number | null;
  unit_price: number | null;
  total_price: number | null;
  matched: boolean;
  match_method: string;
  is_service?: boolean;
  product_id: number | null;
  product: Candidate | null;
  candidates: Candidate[];
  checks: Check[];
}
interface AnalyzeResult {
  import_id: number;
  extraction: any;
  document_type: { detected: string; label: string; warning: string | null };
  customer_match: { customer_id: number | null; customer_company: string | null; method: string; candidates: { id: number; company: string }[]; extracted_name: string };
  destination_match: { shipping_destination_id: number | null; destination: { id: number; name: string } | null; method: string; candidates: { id: number; name: string }[] };
  customer_destinations: { id: number; name: string; city?: string }[];
  lines: AnalyzeLine[];
  checks: Check[];
  missing_fields: string[];
  duplicate_warning: boolean;
  duplicate_order: { order_id: number; order_number: string } | null;
  proposed_order_number: string;
  provider: string;
  model: string;
  filename: string;
  cost: { input_tokens: number | null; output_tokens: number | null; estimated_cost_usd: number | null; duration_ms: number | null };
  budget: { budget_usd: number | null; spent_usd: number; percent: number | null; blocked: boolean; warning: boolean };
}

const METHOD_LABEL: Record<string, { text: string; color: string }> = {
  client_code: { text: 'codice cliente', color: 'green' },
  internal_code: { text: 'codice TEBRO', color: 'green' },
  alias: { text: 'corrispondenza', color: 'green' },
  none: { text: 'da scegliere', color: 'orange' },
};

/**
 * Carica Ordine Cliente con AI (v3.3.0, motore document_import): 1) file → 2) anteprima con
 * correzioni riga per riga → 3) esito. L'AI legge e propone; l'utente conferma; NAVI impara
 * cliente, destinazione e prodotti confermati a mano.
 */
const AIOrderImportModal: React.FC<AIOrderImportModalProps> = ({ visible, onClose, onSuccess }) => {
  const [step, setStep] = useState(0);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<AnalyzeResult | null>(null);
  const [outcome, setOutcome] = useState<any | null>(null);
  const [existingImport, setExistingImport] = useState<{ id: number; message: string } | null>(null);

  const [customers, setCustomers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>();
  const [destinations, setDestinations] = useState<{ id: number; name: string }[]>([]);
  const [selectedDestinationId, setSelectedDestinationId] = useState<number | undefined>();
  const [orderNumber, setOrderNumber] = useState('');
  const [forceDuplicate, setForceDuplicate] = useState(false);
  // scelte per riga: product_id scelto, oppure 'skip'
  const [choices, setChoices] = useState<Record<number, number | 'skip' | undefined>>({});

  useEffect(() => {
    if (visible) {
      setStep(0); setFileList([]); setResult(null); setOutcome(null); setExistingImport(null);
      setSelectedCustomerId(undefined); setDestinations([]); setSelectedDestinationId(undefined);
      setOrderNumber(''); setForceDuplicate(false); setChoices({});
      customerAPI.getCustomers().then((d: any) => setCustomers(Array.isArray(d) ? d : d?.items || [])).catch(() => setCustomers([]));
      productAPI.getProducts().then((d: any) => setProducts(Array.isArray(d) ? d : d?.items || [])).catch(() => setProducts([]));
    }
  }, [visible]);

  // Cambio cliente in anteprima: ricarica le sue destinazioni
  useEffect(() => {
    if (!result) return;
    if (selectedCustomerId === result.customer_match.customer_id) {
      setDestinations(result.customer_destinations || []);
      setSelectedDestinationId(result.destination_match?.shipping_destination_id || undefined);
      return;
    }
    if (!selectedCustomerId) { setDestinations([]); setSelectedDestinationId(undefined); return; }
    shippingDestinationAPI.getShippingDestinations(undefined, selectedCustomerId)
      .then((d: any) => { setDestinations(Array.isArray(d) ? d : d?.items || []); setSelectedDestinationId(undefined); })
      .catch(() => { setDestinations([]); setSelectedDestinationId(undefined); });
  }, [selectedCustomerId, result]);

  const handleAnalyze = async (force = false) => {
    const file = fileList[0]?.originFileObj as File | undefined;
    if (!file) { message.warning('Selezionare un file da analizzare'); return; }
    setAnalyzing(true); setResult(null); setExistingImport(null);
    try {
      const data: AnalyzeResult = await aiOrderImportAPI.analyze(file, force);
      setResult(data);
      setSelectedCustomerId(data.customer_match.customer_id || undefined);
      setOrderNumber(data.proposed_order_number || '');
      const initial: Record<number, number | 'skip' | undefined> = {};
      data.lines.forEach(l => { initial[l.row] = l.is_service ? 'skip' : (l.product_id || undefined); });
      setChoices(initial);
      setStep(1);
      if (!data.lines?.length) message.warning("L'AI non ha individuato righe prodotto nel documento");
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.existing_import_id) {
        setExistingImport({ id: detail.existing_import_id, message: detail.message });
      } else {
        message.error((typeof detail === 'string' ? detail : detail?.message) || "Errore durante l'analisi del documento", 8);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const pendingRows = useMemo(() => (result?.lines || []).filter(l => choices[l.row] === undefined), [result, choices]);
  const chosenCount = useMemo(() => (result?.lines || []).filter(l => typeof choices[l.row] === 'number').length, [result, choices]);

  const handleConfirm = async () => {
    if (!result) return;
    if (!selectedCustomerId) { message.warning("Selezionare il cliente prima di creare l'ordine"); return; }
    if (pendingRows.length) { message.warning(`Ci sono ${pendingRows.length} righe senza scelta: abbina un prodotto o salta la riga`); return; }
    if (chosenCount === 0) { message.warning('Nessuna riga con prodotto: abbina almeno una riga'); return; }
    setConfirming(true);
    try {
      const res = await aiOrderImportAPI.confirm({
        import_id: result.import_id,
        customer_id: selectedCustomerId,
        shipping_destination_id: selectedDestinationId,
        order_number: orderNumber || undefined,
        lines: result.lines.map(l => ({ row: l.row, product_id: typeof choices[l.row] === 'number' ? (choices[l.row] as number) : null, skip: choices[l.row] === 'skip' })),
        force_duplicate: forceDuplicate,
      });
      setOutcome(res); setStep(2); onSuccess();
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      message.error((typeof detail === 'string' ? detail : detail?.message) || "Errore durante la creazione dell'ordine", 8);
    } finally {
      setConfirming(false);
    }
  };

  const handleDiscard = async () => {
    if (result) { try { await aiOrderImportAPI.discard(result.import_id); } catch { /* registro: non bloccante */ } }
    onClose();
  };

  const productOptions = useMemo(() => products.map((p: any) => ({
    value: p.id, label: `${generateProductDisplayName(p.internal_product_code, p.size, p.color)} - ${p.product_description || ''}`,
  })), [products]);

  const lineColumns = [
    { title: '#', dataIndex: 'row', width: 40 },
    {
      title: 'Riga del documento', render: (l: AnalyzeLine) => (
        <div>
          <div>{l.description || '-'}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {l.customer_product_code && <>cod. cliente {l.customer_product_code} · </>}
            {l.internal_product_code && <>cod. TEBRO {l.internal_product_code}</>}
          </Text>
        </div>
      ),
    },
    { title: 'Q.tà', dataIndex: 'quantity', width: 70, align: 'right' as const, render: (v: number | null) => v ?? '-' },
    { title: 'Prezzo', dataIndex: 'unit_price', width: 90, align: 'right' as const, render: (v: number | null) => (v != null ? v.toFixed(4) : '-') },
    {
      title: 'Totale', width: 100, align: 'right' as const, render: (l: AnalyzeLine) => {
        const bad = l.checks.filter(c => !c.ok);
        return (
          <span>
            {l.total_price != null ? l.total_price.toFixed(2) : '-'}{' '}
            {bad.length > 0 && <Tooltip title={bad.map(c => c.message).join(' · ')}><WarningOutlined style={{ color: '#fa8c16' }} /></Tooltip>}
          </span>
        );
      },
    },
    {
      title: 'Prodotto NAVI', width: 380, render: (l: AnalyzeLine) => {
        const choice = choices[l.row];
        const proposed = METHOD_LABEL[l.match_method] || METHOD_LABEL.none;
        const opts = l.candidates.length && !l.matched
          ? [{ label: 'Candidati proposti', options: l.candidates.map(c => ({ value: c.id, label: `${generateProductDisplayName(c.internal_product_code, c.size, c.color)} - ${c.product_description || ''}` })) },
             { label: 'Tutti i prodotti', options: productOptions }]
          : productOptions;
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Space size={4}>
              {choice === 'skip' ? <Tag icon={<CloseCircleOutlined />} color="default">{l.is_service ? 'voce di servizio (trasporto/spese), non è un prodotto: saltata' : 'saltata'}</Tag>
                : l.matched && choice === l.product_id ? <Tag icon={<CheckCircleOutlined />} color={proposed.color}>{proposed.text}</Tag>
                : typeof choice === 'number' ? <Tag color="blue">scelto a mano</Tag>
                : <Tag icon={<WarningOutlined />} color={l.candidates.length ? 'orange' : 'red'}>{l.candidates.length ? 'da confermare' : 'non trovato'}</Tag>}
            </Space>
            <Space.Compact style={{ width: '100%' }}>
              <Select
                style={{ width: '100%' }} showSearch optionFilterProp="label" placeholder="Scegli il prodotto…"
                value={typeof choice === 'number' ? choice : undefined}
                onChange={(v) => setChoices(prev => ({ ...prev, [l.row]: v }))}
                options={opts as any}
                disabled={choice === 'skip'}
              />
              <Button onClick={() => setChoices(prev => ({ ...prev, [l.row]: prev[l.row] === 'skip' ? (l.product_id || undefined) : 'skip' }))}>
                {choice === 'skip' ? 'Ripristina' : 'Salta'}
              </Button>
            </Space.Compact>
          </Space>
        );
      },
    },
  ];

  const custMethod = result?.customer_match.method;
  const custNote = custMethod === 'alias' || custMethod === 'exact' ? null
    : custMethod === 'tokens' ? 'cliente proposto per somiglianza del nome: verifica'
    : custMethod === 'destination' ? 'cliente dedotto dalla nave indicata nel documento: verifica'
    : 'cliente non riconosciuto: selezionalo';

  return (
    <Modal
      open={visible}
      title={<span><RobotOutlined /> Importa con AI - ordine del cliente (PO)</span>}
      width={1100}
      onCancel={step === 2 ? onClose : handleDiscard}
      maskClosable={false}
      className="no-uppercase-modal"
      footer={step === 0 ? [
        <Button key="cancel" onClick={onClose}>Annulla</Button>,
        <Button key="analyze" type="primary" icon={<RobotOutlined />} loading={analyzing} disabled={!fileList.length} onClick={() => handleAnalyze(false)}>Analizza con AI</Button>,
      ] : step === 1 ? [
        <Button key="back" onClick={() => setStep(0)} disabled={confirming}>Indietro</Button>,
        <Button key="discard" onClick={handleDiscard} disabled={confirming}>Scarta</Button>,
        <Button key="confirm" type="primary" icon={<CheckCircleOutlined />} loading={confirming} onClick={handleConfirm}
          disabled={!result || !selectedCustomerId || pendingRows.length > 0 || chosenCount === 0 || (result.duplicate_warning && !forceDuplicate)}>
          Crea ordine
        </Button>,
      ] : [<Button key="close" type="primary" onClick={onClose}>Chiudi</Button>]}
    >
      <Steps size="small" current={step} style={{ marginBottom: 16 }} items={[{ title: 'File' }, { title: 'Anteprima e correzioni' }, { title: 'Esito' }]} />

      {step === 0 && (
        <div>
          <Dragger
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ods,.png,.jpg,.jpeg"
            maxCount={1} fileList={fileList} beforeUpload={() => false}
            onChange={({ fileList: fl }) => { setFileList(fl.slice(-1)); setExistingImport(null); }}
            onRemove={() => { setFileList([]); return true; }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Trascina qui l'ordine del cliente o clicca per selezionarlo</p>
            <p className="ant-upload-hint">PDF, Word, Excel, ODS o immagine (PNG/JPG), massimo 10 MB e 30 pagine. L'analisi può richiedere fino a un minuto.</p>
          </Dragger>
          {existingImport && (
            <Alert type="warning" showIcon style={{ marginTop: 12 }} message={existingImport.message}
              action={<Button size="small" type="primary" loading={analyzing} onClick={() => handleAnalyze(true)}>Rianalizza comunque</Button>} />
          )}
        </div>
      )}

      {step === 1 && result && (
        <div>
          {result.document_type.warning && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={result.document_type.warning} />}
          {result.budget?.blocked && <Alert type="error" showIcon style={{ marginBottom: 12 }} message="Budget mensile AI esaurito." />}
          {result.budget?.warning && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={`Superato l'80% del budget mensile AI (${result.budget.spent_usd.toFixed(2)} $ su ${result.budget.budget_usd?.toFixed(2)} $).`} />}
          {result.duplicate_warning && result.duplicate_order && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              message={`Esiste già l'ordine ${result.duplicate_order.order_number} con lo stesso numero ordine cliente (${result.extraction?.customer_order_number}).`}
              description={<Checkbox checked={forceDuplicate} onChange={e => setForceDuplicate(e.target.checked)}>Crea comunque un nuovo ordine</Checkbox>} />
          )}
          {result.checks.filter(c => !c.ok).map((c, i) => <Alert key={i} type="warning" showIcon style={{ marginBottom: 8 }} message={c.message} />)}
          {result.checks.filter(c => c.ok && c.code === 'merged_rows').map((c, i) => <Alert key={`m${i}`} type="info" showIcon style={{ marginBottom: 8 }} message={c.message} />)}

          <Descriptions size="small" bordered column={2} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="File">{result.filename}</Descriptions.Item>
            <Descriptions.Item label="Modello AI">{result.provider} / {result.model}{result.cost?.estimated_cost_usd != null && <Text type="secondary"> · {result.cost.estimated_cost_usd.toFixed(3)} $</Text>}</Descriptions.Item>
            <Descriptions.Item label="Cliente" span={2}>
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                {result.customer_match.extracted_name && <Text type="secondary">nel documento: {result.customer_match.extracted_name}</Text>}
                <Select style={{ width: 420 }} showSearch optionFilterProp="label" placeholder="Seleziona il cliente"
                  value={selectedCustomerId} onChange={(v) => setSelectedCustomerId(v)}
                  options={customers.map((c: any) => ({ value: c.id, label: c.company }))} />
                {custNote && <Text type={custMethod === 'none' ? 'danger' : 'warning'}>{custNote}</Text>}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="N. ordine cliente (PO)">{result.extraction?.customer_order_number || '-'}</Descriptions.Item>
            <Descriptions.Item label="Numero ordine NAVI">
              <Input value={orderNumber} onChange={e => setOrderNumber(e.target.value.toUpperCase())} style={{ width: 200 }} />
            </Descriptions.Item>
            <Descriptions.Item label="Data ordine">{result.extraction?.order_date || '-'}</Descriptions.Item>
            <Descriptions.Item label="Consegna richiesta">{result.extraction?.requested_delivery_date || '-'}</Descriptions.Item>
            <Descriptions.Item label="Destinazione" span={2}>
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                {result.extraction?.destination && <Text type="secondary">nel documento: {result.extraction.destination}</Text>}
                <Select style={{ width: 420 }} allowClear showSearch optionFilterProp="label"
                  placeholder={destinations.length ? 'Seleziona la destinazione' : 'Il cliente non ha destinazioni in anagrafica'}
                  value={selectedDestinationId} onChange={(v) => setSelectedDestinationId(v)} disabled={!destinations.length}
                  options={destinations.map(d => ({ value: d.id, label: d.name }))} />
                {result.extraction?.destination && !selectedDestinationId && destinations.length > 0 && (
                  <Text type="warning">destinazione «{result.extraction.destination}» non trovata tra quelle del cliente: scegline una o lasciala vuota</Text>
                )}
                {selectedDestinationId && result.destination_match?.method && result.destination_match.method !== 'alias' && result.destination_match.method !== 'exact' && selectedDestinationId === result.destination_match.shipping_destination_id && (
                  <Text type="secondary">abbinata per somiglianza del nome: verifica</Text>
                )}
              </Space>
            </Descriptions.Item>
            {result.missing_fields.length > 0 && (
              <Descriptions.Item label="Campi non individuati" span={2}><Text type="warning">{result.missing_fields.join(', ')}</Text></Descriptions.Item>
            )}
          </Descriptions>

          <Table size="small" rowKey="row" columns={lineColumns} dataSource={result.lines} pagination={false} scroll={{ y: 360 }} />
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              {chosenCount} righe con prodotto · {(result.lines || []).filter(l => choices[l.row] === 'skip').length} saltate · {pendingRows.length} da decidere
            </Text>
          </div>
        </div>
      )}

      {step === 2 && outcome && (
        <Result
          status="success"
          title={`Ordine ${outcome.order_number} creato`}
          subTitle={`${outcome.customer} · ${outcome.details_created} righe${outcome.lines_skipped ? ` · ${outcome.lines_skipped} righe non inserite (vedi note)` : ''}${outcome.shipping_destination ? ` · destinazione ${outcome.shipping_destination}` : ''}`}
          extra={outcome.learned && (outcome.learned.products || outcome.learned.customer || outcome.learned.destination) ? (
            <Alert type="info" showIcon message={`NAVI ha memorizzato le corrispondenze confermate: ${[
              outcome.learned.customer ? 'cliente' : null,
              outcome.learned.destination ? 'destinazione' : null,
              outcome.learned.products ? `${outcome.learned.products} prodotti` : null,
            ].filter(Boolean).join(', ')}. La prossima volta saranno abbinate da sole.`} />
          ) : null}
        />
      )}
    </Modal>
  );
};

export default AIOrderImportModal;
